import { logWarn } from '@ptek/client-logger';
import { useContext } from 'react';
import { reportMalformedHostPayload } from '../config/reportMalformedHostPayload';
import { AppContext } from '../context/AppContext';
import { type FormattedTemplate } from '../types/templates';

interface SetHostTemplatesOptions {
  templates: FormattedTemplate[];
  /**
   * When provided, the widget will automatically select the template whose
   * `id` matches this value. Validated against the full merged template list
   * (host-provided + API templates). If no match is found, a warning is
   * logged and the current selection remains unchanged.
   */
  autoSelectTemplateId?: string;
}

/**
 * Hook to handle host-provided templates (like macro-enabled templates)
 */
export const useHostTemplateHandler = () => {
  const { updateSessionModel } = useContext(AppContext);

  /**
   * Replaces host-provided templates and optionally auto-selects one.
   *
   * @param options.templates - New host-provided templates (replaces previous ones; API templates are preserved).
   * @param options.autoSelectTemplateId - Template ID to select after merging. Logs a warning when unmatched.
   */
  const handleSetHostTemplates = (options: SetHostTemplatesOptions) => {
    if (!options || !Array.isArray(options.templates)) {
      reportMalformedHostPayload(
        'useHostTemplateHandler',
        'Ignoring malformed setTemplates payload (expected templates array):',
        options
      );
      return;
    }

    const { templates, autoSelectTemplateId } = options;

    updateSessionModel((prev) => {
      const nonHostTemplates = prev.templates.filter((t) => !t.isHostProvided);
      const newTemplates = [...nonHostTemplates, ...templates];
      const hasValidSelection = newTemplates.some(
        (template) =>
          template.id === prev.selectedTemplateId && !template.isDisabled
      );

      if (autoSelectTemplateId) {
        const match = newTemplates.find(
          (t) => t.id === autoSelectTemplateId && !t.isDisabled
        );
        if (match) {
          return {
            ...prev,
            templates: newTemplates,
            selectedTemplateId: autoSelectTemplateId,
          };
        }

        logWarn(
          'useHostTemplateHandler',
          `autoSelectTemplateId "${autoSelectTemplateId}" does not match any template in the provided list`
        );
      }

      return {
        ...prev,
        templates: newTemplates,
        selectedTemplateId: hasValidSelection
          ? prev.selectedTemplateId
          : newTemplates.find((template) => !template.isDisabled)?.id || '',
      };
    });
  };

  return { handleSetHostTemplates };
};
